import {
  DECIDE_GATE_INSTANCE_TOOL,
  OPEN_GATE_INSTANCE_TOOL,
  READ_PROCESS_BINDING_TOOL,
  declarationKeyForTool,
  declaredToolGateFromDoorAnswer,
  sha256Hex,
  toolTargetDigest,
} from "./toolGateDeclaration.ts";

/**
 * A Canvas write the person asked for with their own hand (a press on the share sheet, Add file), as the World's one door
 * takes it — the one sequence the Desk's server (apps/server worldDoorHttp.ts canvasDoorHandler, canvasGestureGate.ts) and
 * a window with no Mac behind it (apps/web worldMcp.ts; steps 5-7 design §12a, step 7.4.2) both run:
 *
 * 1. retain: the exact operation is kept privately by the World first (`request-retain`), so a write whose answer is lost
 *    can be found and retried unchanged;
 * 2. the gesture: the step the declaration names for this exact call is opened with the gesture's identity as its request
 *    (`desk-gesture:<action>:<requestKey>`), and decided as the person's own act when it waits for the person
 *    (HUMAN_IN_THE_LOOP: the sheet named the item, the audience and what they may do, and the person pressed);
 * 3. the write itself, which the World's door lets through only with that decided step for this exact target.
 *
 * Only the person's own press runs 2 (the Desk: its person session; a window: a press in the person's own signed-in window).
 * Anything else skips it, and the World's door then opens the step and it waits for the person on a card.
 */

export const CANVAS_TOOL = "neucleos_canvas";

export type ToolOutcome =
  | { readonly ok: true; readonly value: unknown }
  | { readonly ok: false; readonly kind: string; readonly reason: string };
export type CallTool = (tool: string, args: Record<string, unknown>) => Promise<ToolOutcome>;

/** The writes whose exact operation the World keeps before it runs. */
export const CANVAS_RETAINED_ACTIONS: ReadonlyArray<string> = Object.freeze([
  "save",
  "canvas-share",
  "canvas-share-snapshot",
  "canvas-share-revoke",
  "canvas-public-publish",
  "canvas-friend-link",
  "canvas-friend-link-remove",
  "canvas-unpublish",
  "canvas-offer",
  "canvas-offer-withdraw",
]);

/** The writes a person's own press attests as their request. */
export const CANVAS_ATTESTED_ACTIONS: ReadonlyArray<string> = Object.freeze([
  "save",
  "request-acknowledge",
  "dataset-revoke",
  "action-approve",
  "action-cancel",
  "action-commit",
  "canvas-share",
  "canvas-share-snapshot",
  "canvas-share-revoke",
  "canvas-friend-link-remove",
  "canvas-unpublish",
  "group-create",
  "group-member-add",
  "group-member-remove",
  "group-retire",
  "canvas-offer",
  "canvas-offer-withdraw",
  "canvas-offer-accept",
  "canvas-offer-decline",
]);

export const liveCanvasShareLabel = (request: Record<string, unknown>): string => {
  const actions = Array.isArray(request["actions"]) ? request["actions"] : [];
  const right = actions.includes("edit") ? "can edit" : "can view";
  if (typeof request["dependencyDigest"] !== "string") return `Share live Canvas (${right})`;
  return request["dataChanges"] === true
    ? `Share live Canvas (${right}) with its Components and collections, readable and changeable outside it`
    : `Share live Canvas (${right}) with its Components and collections, readable outside it`;
};

/** The retained operation's label: what the person's recovery list says it was (at most 160 characters). */
export function retainLabel(action: string, request: Record<string, unknown>): string {
  return (
    action === "save"
      ? `Save ${String(request["title"] ?? "Canvas revision")}`
      : action === "canvas-public-publish"
        ? `Publish ${String(request["path"] ?? "Canvas")}`
        : action === "canvas-friend-link"
          ? "Share a link with a friend"
          : action === "canvas-friend-link-remove"
            ? "Take a friend link down"
            : action === "canvas-unpublish"
              ? `Unpublish ${String(request["path"] ?? "page")}`
              : action === "canvas-offer"
                ? `Offer to ${String(request["destinationWorldKey"] ?? "another World")}`
                : action === "canvas-offer-withdraw"
                  ? "Withdraw an offer to another World"
                  : action === "canvas-share-revoke"
                    ? "Remove Canvas access"
                    : action === "canvas-share-snapshot"
                      ? "Share fixed Canvas copy"
                      : liveCanvasShareLabel(request)
  ).slice(0, 160);
}

/** The `request-retain` input for one write (publish and friend-link keep their authored operands only). */
export function retainedInputFor(action: string, request: Record<string, unknown>): Record<string, unknown> {
  const { hostScript: _hostScript, hostStyle: _hostStyle, ...authored } = request;
  const operation = { ...(action === "canvas-public-publish" || action === "canvas-friend-link" ? authored : request), action };
  return {
    action: "request-retain",
    canvasId: request["canvasId"],
    requestKey: request["requestKey"],
    label: retainLabel(action, request),
    operation,
  };
}

export type GestureOutcome =
  | { readonly ok: true; readonly gateKey: string | null; readonly targetDigest: string | null }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

/**
 * The person's own press, attested as the request for this exact Canvas call (CVC6, Arc ONE 4.1). Nothing is opened when
 * no declared step applies to the call, or the declaration says it needs no decision, or asks the person (that is for
 * Cleo's and a connector's calls). `onDecided` hears an approval this press decided (the Desk tells its held calls).
 */
export async function attestCanvasGesture(input: {
  readonly call: CallTool;
  readonly toolInput: Record<string, unknown>;
  readonly requestKey: string;
  readonly now?: () => number;
  readonly onDecided?: (gateInstanceKey: string, answered: unknown) => void;
}): Promise<GestureOutcome> {
  const binding = await input.call(READ_PROCESS_BINDING_TOOL, { kind: "tool", key: declarationKeyForTool(CANVAS_TOOL) });
  if (!binding.ok) return binding;
  const declared = declaredToolGateFromDoorAnswer(binding.value, { toolName: CANVAS_TOOL, toolInput: input.toolInput });
  if (!declared || declared.noDecision || declared.askPerson) return { ok: true, gateKey: null, targetDigest: null };
  const targetDigest = toolTargetDigest(CANVAS_TOOL, input.toolInput);
  // One durable action request has separate approval, commit and cancellation gestures. Reuse the occurrence only within
  // its phase, so a changed payload still refuses instead of silently acquiring another authorization.
  const occurrenceKey = `desk-gesture:${String(input.toolInput["action"])}:${input.requestKey}`;
  const opened = await input.call(OPEN_GATE_INSTANCE_TOOL, {
    gateKey: declared.gateKey,
    ...(declared.stepKey ? { stepKey: declared.stepKey } : {}),
    targetDigest,
    occurrenceKey,
    request: { messageDigest: sha256Hex(occurrenceKey), requestedAt: (input.now ?? Date.now)() },
  });
  if (!opened.ok) return opened;
  const instance = opened.value as { readonly gateInstanceKey?: unknown; readonly state?: unknown } | null;
  if (instance?.state === "CREATED" && typeof instance.gateInstanceKey === "string") {
    const decided = await input.call(DECIDE_GATE_INSTANCE_TOOL, {
      gateInstanceKey: instance.gateInstanceKey,
      targetDigest,
      outcome: "approved",
      decisionKey: occurrenceKey,
    });
    if (!decided.ok) return decided;
    input.onDecided?.(instance.gateInstanceKey, decided.value);
  }
  return { ok: true, gateKey: declared.gateKey, targetDigest };
}

const refusal = (kind: string, reason: string) => ({ ok: false as const, kind, reason });

/**
 * Steps 1 and 2 for one write (retain when the action is retained, the gesture when it is attested), before the caller
 * makes the write itself. `personPress` false skips the gesture (a script, or nothing pressed by the person).
 */
export async function prepareCanvasPersonWrite(input: {
  readonly call: CallTool;
  readonly action: string;
  readonly request: Record<string, unknown>;
  readonly personPress: boolean;
  readonly now?: () => number;
  readonly onDecided?: (gateInstanceKey: string, answered: unknown) => void;
}): Promise<{ readonly ok: true } | { readonly ok: false; readonly kind: string; readonly reason: string }> {
  const { action, request } = input;
  const text = (value: unknown) => (typeof value === "string" && value.trim() ? value : undefined);
  if (CANVAS_RETAINED_ACTIONS.includes(action)) {
    const requestKey = text(request["requestKey"]);
    const canvasId = text(request["canvasId"]);
    if (!requestKey || !canvasId) return refusal("bad_request", "A Canvas operation names its Canvas and request identity.");
    const retainedInput = retainedInputFor(action, request);
    if (input.personPress) {
      const admitted = await attestCanvasGesture({ call: input.call, toolInput: retainedInput, requestKey, ...(input.now ? { now: input.now } : {}), ...(input.onDecided ? { onDecided: input.onDecided } : {}) });
      if (!admitted.ok) return admitted;
    }
    const retained = await input.call(CANVAS_TOOL, retainedInput);
    if (!retained.ok) return retained;
    const value = retained.value as { requestKey?: unknown; gateRefusal?: unknown } | null;
    if (value?.gateRefusal || value?.requestKey !== requestKey)
      return refusal("upstream_refusal", "The original request could not be retained. Its effect was not dispatched.");
  }
  if (CANVAS_ATTESTED_ACTIONS.includes(action)) {
    const requestKey = text(request["requestKey"]);
    if (!requestKey) return refusal("bad_request", "A save names its requestKey.");
    if (input.personPress) {
      const attested = await attestCanvasGesture({ call: input.call, toolInput: { ...request, action }, requestKey, ...(input.now ? { now: input.now } : {}), ...(input.onDecided ? { onDecided: input.onDecided } : {}) });
      if (!attested.ok) return attested;
    }
  }
  return { ok: true };
}
